import { FmTask } from '@core/api';
import { DocumentType, DocumentVisibility } from '@shared/types/document.types';

export interface ServiceRegistrationDocumentEntry {
  documentId?: string;
  documentTypeId: string;
  visibilityId: string;
  file: File;
  name: string;
  filename: string;
}

export function getServiceRegistrationDocumentsByQuotationLineId(
  task?: FmTask,
  defaultDocumentTypeId?: string
): Map<string, ServiceRegistrationDocumentEntry[]> {
  const documentsByQuotationLineId = new Map<
    string,
    ServiceRegistrationDocumentEntry[]
  >();

  const currentTaskDocuments = Array.isArray(task?.fmDocument)
    ? task.fmDocument
    : [];
  const incidentTaskDocuments = (
    Array.isArray(task?.incident?.fmTask) ? task?.incident?.fmTask : []
  )
    .filter(incidentTask => incidentTask?.taskId === task?.taskId)
    .flatMap(incidentTask => incidentTask?.fmDocument || []);

  const allDocuments = [...currentTaskDocuments, ...incidentTaskDocuments];

  const dedupedDocuments = allDocuments.filter((document, index) => {
    const key = `${document?.documentId || ''}|${document?.name || ''}|${resolveQuotationLineId(document) || ''}`;
    return (
      allDocuments.findIndex(candidate => {
        const candidateKey = `${candidate?.documentId || ''}|${candidate?.name || ''}|${resolveQuotationLineId(candidate) || ''}`;
        return candidateKey === key;
      }) === index
    );
  });

  for (const document of dedupedDocuments) {
    const quotationLineId = resolveQuotationLineId(document);
    if (!quotationLineId) {
      continue;
    }

    const existingEntries =
      documentsByQuotationLineId.get(quotationLineId) || [];
    documentsByQuotationLineId.set(quotationLineId, [
      ...existingEntries,
      toServiceRegistrationDocumentEntry(document, defaultDocumentTypeId),
    ]);
  }

  return documentsByQuotationLineId;
}

function resolveQuotationLineId(document: any): string | undefined {
  return (
    document?.quotationLineId ||
    document?.quotationLine?.id ||
    document?.quotationLine?.quotationLineId ||
    undefined
  );
}

export function prepareServiceRegistrationLocation(
  location: any,
  options?: {
    includePurchaseOrderCompletionDateFallback?: boolean;
  }
): any {
  const quantityProvided = (location as any).quantityProvided ?? 0;
  const unitPrice = parseFloat(String((location as any).unitPrice)) || 0;
  const includePurchaseOrderCompletionDateFallback =
    options?.includePurchaseOrderCompletionDateFallback ?? false;

  return {
    ...location,
    quantityProvided,
    serviceRegistrationBillableValue:
      (parseFloat(String(quantityProvided)) || 0) * unitPrice,
    serviceRegistrationCompletionDate:
      (location as any).serviceRegistrationCompletionDate ||
      (includePurchaseOrderCompletionDateFallback
        ? (location as any)?.purchaseOrder?.completionDate
        : null) ||
      null,
    serviceRegistrationDocumentationFiles:
      (location as any).serviceRegistrationDocumentationFiles || [],
    serviceRegistrationCommentExternal:
      (location as any).serviceRegistrationCommentExternal || '',
    serviceReported: !!(location as any).serviceReported,
  };
}

export function prepareServiceRegistrationLocations(
  locations: any[],
  options?: {
    includePurchaseOrderCompletionDateFallback?: boolean;
  }
): any[] {
  return (locations || []).map(location =>
    prepareServiceRegistrationLocation(location, options)
  );
}

function toServiceRegistrationDocumentEntry(
  document: any,
  defaultDocumentTypeId?: string
): ServiceRegistrationDocumentEntry {
  const fileName = document?.name || document?.filename || 'Dokument';
  const file = createExistingDocumentReferenceFile(
    fileName,
    document?.documentId
  );

  return {
    documentId: document?.documentId,
    documentTypeId:
      document?.documentTypeId ||
      defaultDocumentTypeId ||
      DocumentType.Sonstiges,
    visibilityId:
      document?.visibilityId || DocumentVisibility.InternAndContractors,
    file,
    name: fileName,
    filename: document?.filename || fileName,
  };
}

function createExistingDocumentReferenceFile(
  fileName: string,
  documentId?: string
): File {
  const file = new File([''], fileName || 'Dokument', {
    type: 'application/octet-stream',
  });

  if (documentId) {
    (file as any).documentId = documentId;
  }

  return file;
}
